import React from 'react';
import { useLocation } from 'react-router-dom';
import { List, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { BackstageTheme } from '@backstage/theme';
import { PichinchaMenuItem } from '../common/PichinchaMenuItem';
import { PichinchaSubmenuItem } from '../common/PichinchaSubmenuItem';
import { useIsAdmin } from '../auth';
import { menuConfiguration } from '../../config/menuConfig';
import { useIconResolver } from '../../hooks/useIconResolver';
import { MenuItem } from '../../types/menu';

const useStyles = makeStyles((theme: BackstageTheme) => ({
  sectionDivider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.divider,
  },
  listItemContainer: {
    position: 'relative',
  },
}));

interface DynamicMenuProps {
  isMenuCollapsed: boolean;
}

export const DynamicMenu: React.FC<DynamicMenuProps> = ({ isMenuCollapsed }) => {
  const classes = useStyles();
  const location = useLocation();
  const { isAdmin } = useIsAdmin();
  const { resolveIcon } = useIconResolver();

  // Filtrar elementos basados en permisos de administrador
  const filteredMenuGroups = menuConfiguration.filter(group => 
    !group.adminOnly || isAdmin
  );

  // Debug: console para verificar permisos administrativos
  console.log('DynamicMenu Debug:', {
    isAdmin,
    totalGroups: menuConfiguration.length,
    filteredGroups: filteredMenuGroups.length,
    menuConfiguration: menuConfiguration.map(g => ({ id: g.id, adminOnly: g.adminOnly }))
  });

  // Verificar si una ruta está activa o es parte de un submenú activo
  const isPathActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isSubmenuActive = (subItems: MenuItem['subItems']) => {
    if (!subItems) return false;
    return subItems.some(subItem => isPathActive(subItem.path));
  };

  // Renderizar un item individual del menú
  const renderMenuItem = (item: MenuItem) => {
    // Filtrar elementos administrativos si el usuario no es admin
    if (item.adminOnly && !isAdmin) {
      return null;
    }

    const icon = resolveIcon(item.icon);

    // Si tiene subitems, es un submenu
    if (item.subItems && item.subItems.length > 0) {
      // En menú expandido, mostrar como submenu
      if (!isMenuCollapsed) {
        const subItems = item.subItems.map(subItem => ({
          text: subItem.text,
          path: subItem.path,
          icon: resolveIcon(subItem.icon)
        }));

        return (
          <PichinchaSubmenuItem
            key={item.id}
            text={item.text}
            icon={icon}
            subItems={subItems}
            isActive={isSubmenuActive(item.subItems)}
          />
        );
      } else {
        // En menú colapsado, mostrar subitems como elementos individuales
        return (
          <React.Fragment key={item.id}>
            {item.subItems.map((subItem, subIndex) => (
              <PichinchaMenuItem
                key={`${item.id}-${subIndex}`}
                text={subItem.text}
                icon={resolveIcon(subItem.icon)}
                path={subItem.path}
                isCollapsed={isMenuCollapsed}
              />
            ))}
          </React.Fragment>
        );
      }
    }

    // Item individual
    return (
      <PichinchaMenuItem
        key={item.id}
        text={item.text}
        icon={icon}
        path={item.path}
        isCollapsed={isMenuCollapsed}
      />
    );
  };

  // Renderizar un grupo del menú
  const renderMenuGroup = (group: any, groupIndex: number) => {
    const filteredItems = group.items.filter((item: MenuItem) => !item.adminOnly || isAdmin);
    
    // No renderizar grupos vacíos
    if (filteredItems.length === 0) {
      return null;
    }

    // Caso especial para el grupo de búsqueda - lo omitimos ya que se maneja en Root.tsx
    if (group.id === 'search') {
      return null;
    }

    const shouldShowDivider = groupIndex < filteredMenuGroups.length - 1;

    return (
      <React.Fragment key={group.id}>
        {filteredItems.map((item: MenuItem) => (
          <div key={item.id} className={classes.listItemContainer}>
            {renderMenuItem(item)}
          </div>
        ))}
        {shouldShowDivider && (
          <Divider className={classes.sectionDivider} />
        )}
      </React.Fragment>
    );
  };

  return (
    <List id="sidebar-menu">
      {filteredMenuGroups.map(renderMenuGroup)}
    </List>
  );
};
